import GaiaIcon from '@gaia/icons/GaiaIcon';
import {MapboxControlButton, MapboxControlGroup} from '@gaia/utils/mapbox/MapboxControl';
import SubscriptionTooltip from '@map/SubscriptionTooltip';
import type mapboxgl from 'mapbox-gl';
import {useCallback, useEffect, useState} from 'react';

import classes from './Controls.module.css';

export interface NavigationControlProps {
    map: mapboxgl.Map | undefined;
}

export function NavigationControl({map}: NavigationControlProps) {
    const [isZoomInDisabled, setZoomInDisabled] = useState(false);
    const [isZoomOutDisabled, setZoomOutDisabled] = useState(false);

    const handleZoomEnd = useCallback(() => {
        if (map) {
            const zoom = map.getZoom();
            setZoomInDisabled(zoom === map.getMaxZoom());
            setZoomOutDisabled(zoom === map.getMinZoom());
        }
    }, [map]);

    // Update the control buttons' state when the zoom level changes on the map.
    useEffect(() => {
        if (!map) return;
        map.on('zoomend', handleZoomEnd);
        return () => {
            map.off('zoomend', handleZoomEnd);
        };
    }, [map, handleZoomEnd]);

    return (
        <MapboxControlGroup>
            <MapboxControlButton
                disabled={isZoomInDisabled}
                aria-label={'Zoom in'}
                className={classes.mapControl}
                onClick={() => {
                    if (!map) return;
                    map.zoomIn();
                }}
            >
                <SubscriptionTooltip
                    enterDelay={500}
                    placement='right'
                    title='Zoom In'
                    subscription={undefined}
                >
                    <GaiaIcon name='zoom-in' style={{height: 24, width: 24}} />
                </SubscriptionTooltip>
            </MapboxControlButton>

            <MapboxControlButton
                disabled={isZoomOutDisabled}
                aria-label='Zoom out'
                className={classes.mapControl}
                onClick={() => {
                    if (!map) return;
                    map.zoomOut();
                }}
            >
                <SubscriptionTooltip
                    enterDelay={500}
                    placement='right'
                    title='Zoom Out'
                    subscription={undefined}
                >
                    <GaiaIcon name='zoom-out' style={{height: 24, width: 24}} />
                </SubscriptionTooltip>
            </MapboxControlButton>
        </MapboxControlGroup>
    );
}
